import * as React from "react";
import { CircularProgress } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { DateTime } from "luxon";
import { useSwipeable } from "react-swipeable";
import pluralize from "pluralize";

// components
import { TimesForDay } from "@ui/times-for-day";
import { ISuggestedSlotsByDate } from "../scheduling-link";
import ShowEarliestButton from "./ShowEarliestButton/";

// types
import { ISlotTimeRange } from "../scheduling-link";

// ui, styles, and animation.
import { animated, useTransition, useSpring, config } from "@react-spring/web";
import { HoverableChevron } from "@ui/hoverable-chevron";
import { narrowCutoff } from "@ui/scheduling-link/scheduling-link.styles";

// util
import { useElementDimensions, useWindowSize } from "@util/react.util";
import { classNames } from "@util/html.util";
import { DateString } from "@ui/scheduling-link/scheduling-link";

// styles
import { greys, colors } from "@styles/color.styles";
import { fontFamilyHelvetica, fontFamilyIdealSans } from "@styles/type.styles";

export interface TimesForDaysProps {
  days: ISuggestedSlotsByDate[];
  currentDate?: DateString | null;
  onChangeDate: (date: DateString) => void;
  onSelectTime: (timeRange: ISlotTimeRange) => void;
  loading: boolean;
  trackSession: (event: string, data?: Record<string, any>) => void;
  highlightBestTimes: boolean;
}

function nonNullable<T>(value: T): value is NonNullable<T> {
  return value !== null && value !== undefined;
}

const DAY_MARGIN_LEFT = 8;
const DAY_MARGIN_RIGHT = 8;

export const narrowMediaQuery = `@media (max-width: ${narrowCutoff}px)`;

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    overflow: "auto",
  },
  topActions: {
    justifyContent: "space-between",
    alignItems: "center",
    paddingBottom: 26,
    marginLeft: 12,
  },
  topRightActions: {
    display: "flex",
    alignItems: "center",
    marginRight: 9,

    "& > svg": {
      fontSize: "1.8rem",
    },
  },
  loadingSpinner: {
    marginRight: 6,
  },
  header: {
    fontFamily: fontFamilyIdealSans,
    fontSize: "1.25rem",
    fontWeight: "bold",
    paddingBottom: "1rem",
    marginTop: 0,
    color: colors.blueDusk.darker,
    background: "linear-gradient(180deg, white 75%, rgba(255, 255, 255, 0) 110%)",

    // To hide the content behind the header as it scrolls up
    paddingLeft: 10,
    marginLeft: -10,
    paddingRight: 10,
    marginRight: -10,
    // Sticky headers
    top: 0,
    zIndex: 10,
    position: "sticky",

    [narrowMediaQuery]: {
      position: "unset",
    },
  },
  daysContainer: {
    display: "flex",
    paddingBottom: 5,
    paddingLeft: 6,
    overflowX: "hidden",
  },
  bold: {
    fontWeight: "bold",
  },
  selectATime: {
    color: greys.slate,
    fontSize: "1.02rem",
    fontFamily: fontFamilyHelvetica,
    verticalAlign: "middle",
    flexShrink: 0,
    marginRight: 20,
  },
  day: {
    marginRight: DAY_MARGIN_RIGHT,
    marginLeft: DAY_MARGIN_LEFT,

    "@media (max-width: 450px)": {
      flexShrink: 0,
    },

    "&.hidden": {
      visibility: "hidden",
    },
  },
  hidden: {
    opacity: 0,
  },
  giveFeedback: {
    color: colors.blueDusk.darker,
    fontSize: `${14 / 16}rem`,
    fontFamily: fontFamilyHelvetica,
    marginRight: 10,
    padding: "5px 12px",
    border: `1px solid ${greys.cloudyBlue}`,
    borderRadius: 46,
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    overflow: "hidden",
    whiteSpace: "nowrap",

    "& > svg": {
      marginRight: 5,
    },

    "&:hover": {
      backgroundColor: greys.palePaleGrey,
    },
  },
});

// Takes in a list of days, and renders them in a paginated carousel.
// As we preemptively fetch more days and update the days props, genDaysToRender updates
// daysToRender, which is the array of days passed to react-spring, useTransition to render.

export const MAX_DAYS_TO_RENDER = 5;

// These constants are exports to be used in calculations in other components.
export const DAY_WIDTH_PRE_MARGIN = 211;
export const TOTAL_DAY_WIDTH = DAY_WIDTH_PRE_MARGIN + DAY_MARGIN_LEFT + DAY_MARGIN_RIGHT; // 16 accounts for 8 left and 8 right padding.

export const TimesForDays = ({
  days,
  currentDate,
  onChangeDate,
  onSelectTime,
  loading,
  trackSession,
  highlightBestTimes,
}: TimesForDaysProps) => {
  const classes = useStyles();

  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const { width } = useElementDimensions(containerRef.current, 3);
  const { width: windowWidth } = useWindowSize();
  const isNarrow = windowWidth && windowWidth < narrowCutoff;

  const startIndex = React.useMemo(() => {
    if (!currentDate) return 0;

    const currentDateTime = DateTime.fromISO(currentDate);
    const index = days.findIndex(
      (day) =>
        day.suggestedSlots.length && DateTime.fromISO(day.date).hasSame(currentDateTime, "day"),
    );

    return index >= 0 ? index : 0;
  }, [days, currentDate]);

  const numDaysToRender = React.useMemo(() => {
    if (isNarrow) {
      return 1;
    }

    return Math.min(Math.floor(width / TOTAL_DAY_WIDTH), MAX_DAYS_TO_RENDER) || 1;
  }, [width, isNarrow]);

  // Adding numDays to give us an idea of the window width users are viewing the link with.
  const trackSessionWithNumDays = React.useCallback(
    (event: string, data?: Record<string, any>) =>
      trackSession(event, {
        ...data,
        numDaysInView: numDaysToRender,
      }),
    [trackSession, numDaysToRender],
  );

  const onLeft = () => {
    const newIndex = Math.max(0, startIndex - numDaysToRender);
    onChangeDate(days[newIndex]?.date);
  };

  const onRight = () => {
    const newIndex = Math.min(startIndex + numDaysToRender, days.length - numDaysToRender);
    onChangeDate(days[newIndex]?.date);
  };

  const swipeHandlers = useSwipeable({
    onSwipedLeft: onRight,
    onSwipedRight: onLeft,
  });

  const daysToRender = React.useMemo(
    () =>
      days
        .map((day: ISuggestedSlotsByDate, i: number) => {
          if (!day.suggestedSlots.length) {
            return;
          }

          // Only need to populate enough days to render out of view to the right in order
          // to be able to scroll them in and out nicely.
          if (i >= startIndex + numDaysToRender * 2) {
            return;
          }

          const dayVisible = i >= startIndex && i < startIndex + numDaysToRender;

          return (
            <div key={day.date} className="cw-h-full">
              <h2 className={classes.header} cw-id={dayVisible ? "link-day-header" : undefined}>
                {DateTime.fromISO(day.date).toLocaleString({
                  weekday: "short",
                  month: "short",
                  day: "numeric",
                })}
              </h2>
              <TimesForDay
                times={day.suggestedSlots}
                onSelectTime={onSelectTime}
                trackSession={trackSessionWithNumDays}
                highlightBestTimes={highlightBestTimes}
                className="cw-pb-[1px]"
                dayVisible={dayVisible}
              />
            </div>
          );
        })
        .filter(nonNullable),
    [
      classes.header,
      days,
      highlightBestTimes,
      numDaysToRender,
      onSelectTime,
      startIndex,
      trackSessionWithNumDays,
    ],
  );

  const transitions = useTransition(daysToRender, {
    trail: startIndex > 4 ? 0 : 200, // Stop trail delay after first items render.
    from: { opacity: 0 }, // The default values.
    enter: { opacity: 1 }, // The value that it animates to upon enter.
    immediate: true, // Don't animate anything after the first items.
    keys: (day) => day.key ?? JSON.stringify(day.props).substr(0, 15),
    // Should be just day.key, but it needs a fallback incase it is undefined.
  });

  // Spring to animate page left and page right.
  const leftRightSpring = useSpring(
    isNarrow
      ? {
          x: -startIndex * (width - 12), // 12 acconuts for left and right padding.
          containerWidth: 0, // Not used, react spring needs a number here though.
          config: { ...config.stiff }, // Stiff to make swiping left/right feel more responsive.
        }
      : {
          x: -startIndex * TOTAL_DAY_WIDTH,
          containerWidth:
            TOTAL_DAY_WIDTH * Math.min(numDaysToRender, days.length - startIndex) + 14,
          config: { ...config.default },
        },
    [startIndex, numDaysToRender, daysToRender, isNarrow, width],
  );

  const [{ x, containerWidth }] = leftRightSpring;
  const hasMoreDays = startIndex + numDaysToRender < days.length;

  return (
    <div className={classes.container} ref={containerRef}>
      <animated.div
        className={classNames(["cw-flex", classes.topActions])}
        style={{ maxWidth: isNarrow ? "100%" : containerWidth }}
      >
        <div className={classes.selectATime}>Choose a time</div>
        <div className={classes.topRightActions}>
          <CircularProgress
            style={{ visibility: loading ? "visible" : "hidden" }}
            size={18}
            color="primary"
            className={classes.loadingSpinner}
          />

          <ShowEarliestButton
            currentDate={currentDate || ""}
            days={days}
            disabled={loading}
            onShowEarliest={onChangeDate}
            style={{ display: isNarrow ? "none" : "inherit" }}
          />

          <HoverableChevron
            direction="left"
            onClick={onLeft}
            disabled={startIndex === 0}
            cw-id="link-prev-week-btn"
            aria-label={
              startIndex === 0
                ? "No earlier availability"
                : `Previous ${pluralize("day", numDaysToRender, true)} with availability`
            }
          />
          <HoverableChevron
            direction="right"
            onClick={onRight}
            disabled={!hasMoreDays}
            cw-id="link-next-week-btn"
            aria-label={
              hasMoreDays
                ? `Next ${pluralize("day", numDaysToRender, true)} with availability`
                : "No further availability"
            }
          />
        </div>
      </animated.div>
      <animated.div
        {...swipeHandlers}
        className={classNames([classes.daysContainer])}
        style={{
          maxWidth: isNarrow ? "100%" : containerWidth,
          overflowY: isNarrow ? "hidden" : "auto",
        }}
      >
        <animated.div
          className="cw-flex cw-h-full"
          style={{
            x,
          }}
        >
          {transitions(({ opacity }, day) => {
            return (
              <animated.div
                className={classNames([classes.day])}
                style={{
                  opacity,
                  width: isNarrow ? width - 28 : DAY_WIDTH_PRE_MARGIN,
                  maxHeight: isNarrow // 40 height of date header, 45 height of time card, 8 time card margin top.
                    ? 40 + (45 + 8) + days[startIndex]?.suggestedSlots.length * (45 + 8) || "100%"
                    : "100%",
                }}
                key={day.key}
              >
                {day}
              </animated.div>
            );
          })}
        </animated.div>
      </animated.div>
    </div>
  );
};
